import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import { useNotifications } from '../../hooks/useNotifications';
import notificationService from '../../../services/notificationService';

const Messages = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false); // Moved up
  const limit = 10;
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const {
    notifications,
    loading,
    handleMarkAllAsRead,
    handleDeleteNotification,
    handleDeleteAll,
    handleBulkAction,
    refreshNotifications,
    pagination,
    unreadCount
  } = useNotifications(currentPage, limit, showUnreadOnly ? 'unread' : 'all');

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedNotifications, setExpandedNotifications] = useState({});

  // Handle notification link clicks
  useEffect(() => {
    const handleLinkClick = (e) => {
      const link = e.target.closest('a[data-link]');
      if (!link) return;

      e.preventDefault();
      const dataLink = link.getAttribute('data-link');
      
      if (!dataLink) return;

      // Handle relative paths (e.g., /airliner/aircraft/4)
      if (dataLink.startsWith('/')) {
        navigate(dataLink);
      } 
      // Handle full URLs - extract path from URL
      else if (dataLink.startsWith('http')) {
        try {
          const url = new URL(dataLink);
          navigate(url.pathname);
        } catch (error) {
          console.error('Invalid URL:', dataLink);
        }
      }
    };

    if (contentRef.current) {
      contentRef.current.addEventListener('click', handleLinkClick);
      return () => {
        contentRef.current?.removeEventListener('click', handleLinkClick);
      };
    }
  }, [navigate]);

  // Transform notifications to match UI structure
  const messages = useMemo(() => {
    let filtered = notifications.map((notif, index) => ({
      id: notif.id || `temp-${index}`, // Fallback for null IDs
      subject: notif.title,
      content: notif.content,
      isRead: notif.read === 1,
      selected: selectedIds.includes(notif.id),
      created_at: notif.created_at,
      formattedDate: new Date(notif.created_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }),
      formattedTime: new Date(notif.created_at).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }));

    if (showUnreadOnly) {
      filtered = filtered.filter(msg => !msg.isRead);
    }

    return filtered;
  }, [notifications, selectedIds, showUnreadOnly]);

  // Toggle notification expansion and mark as read
  const handleToggleExpand = async (id, isRead, originalId) => {
    // Use the display ID for state management
    setExpandedNotifications(prev => ({
      ...prev,
      [id]: !prev[id]
    }));

    // Only mark as read if we have a real ID (not a temp one) and it's unread
    if (originalId && !isRead && !expandedNotifications[id]) {
      try {
        await notificationService.markAsRead(originalId);
        refreshNotifications();
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      setSelectedIds(messages.map(msg => msg.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectMessage = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(selectedId => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleDeleteAllClick = () => {
    setDeleteTarget('all');
    setShowDeleteModal(true);
  };

  const handleMarkAllAsReadClick = () => {
    handleMarkAllAsRead();
    toast.success('All messages marked as read.');
  };

  const handleShowOnlyUnread = () => {
    setShowUnreadOnly(!showUnreadOnly);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const handleDeleteMessageClick = (id) => {
    setDeleteTarget(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteTarget === 'all') {
      await handleDeleteAll();
    } else {
      await handleDeleteNotification(deleteTarget);
    }
    setDeleteTarget(null);
    setSelectedIds([]);
    setSelectAll(false);
    refreshNotifications();
  };

  const getDeleteModalContent = () => {
    if (deleteTarget === 'all') {
      return {
        title: 'Delete Message(s)?',
        message: 'You are about to delete all messages. Click "DELETE" to proceed or "CANCEL" to exit.'
      };
    } else {
      return {
        title: 'Delete Message(s)?',
        message: 'You are about to Delete one or more message(s). Click "DELETE" to proceed or "CANCEL" to exit.'
      };
    }
  };

  return (
    <div className="p-0 lg:ml-6 bg-jet-gray-100 min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif] flex flex-col">
      <div className="flex-1">
        <div className="py-5 px-[30px] mb-0 bg-transparent">
          <h1 className="m-0 text-lg text-[#333] font-bold">Messages</h1>
        </div>
        <div className="bg-white p-[15px]">
          <div className="flex justify-between items-center py-[15px] px-[30px] bg-transparent mb-[15px] lg:flex-row flex-col lg:gap-0 gap-[15px] lg:items-center items-start">
            <div className="flex items-center gap-2.5">
              <input
                type="checkbox"
                checked={messages.length > 0 && selectedIds.length === messages.length}
                onChange={handleSelectAll}
                className="w-[18px] h-[18px] cursor-pointer accent-[#eb910c]"
                title="Select all on this page"
              />
              <div className="relative">
                <button
                  className="text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border cursor-pointer font-normal whitespace-nowrap hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  Select Action ▼
                </button>
                {showActionDropdown && (
                  <div className="absolute top-full left-0 bg-[#ffeb00] border border-[#ddd] rounded shadow-[0_4px_12px_rgba(0,0,0,0.15)] z-10 min-w-[150px] mt-[5px] overflow-hidden">
                    <div
                      onClick={() => {
                        handleDeleteAllClick();
                        setShowActionDropdown(false);
                      }}
                      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold transition-colors duration-200 hover:bg-[#e6d300] flex items-center gap-2"
                    >
                      <span className="text-[12px]">✓</span> Delete
                    </div>
                    <div
                      onClick={() => {
                        if (selectedIds.length === 0) {
                          toast.error('Please select at least one message.');
                          setShowActionDropdown(false);
                          return;
                        }
                        handleBulkAction('mark_read', selectedIds).then(() => {
                          toast.success(`${selectedIds.length} message(s) marked as read.`);
                          setSelectedIds([]);
                          setSelectAll(false);
                          refreshNotifications();
                        });
                        setShowActionDropdown(false);
                      }}
                      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold transition-colors duration-200 hover:bg-[#e6d300] flex items-center gap-2"
                    >
                      <span className="text-[12px]">✓</span> Mark As Read
                    </div>
                    <div
                      onClick={() => {
                        if (selectedIds.length === 0) {
                          toast.error('Please select at least one message.');
                          setShowActionDropdown(false);
                          return;
                        }
                        handleBulkAction('mark_unread', selectedIds).then(() => {
                          toast.success(`${selectedIds.length} message(s) marked as unread.`);
                          setSelectedIds([]);
                          setSelectAll(false);
                          refreshNotifications();
                        });
                        setShowActionDropdown(false);
                      }}
                      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold transition-colors duration-200 hover:bg-[#e6d300] flex items-center gap-2"
                    >
                      <span className="text-[12px]">✓</span> Mark As Unread
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2.5 lg:w-auto w-full lg:justify-end justify-start flex-wrap">
              <button 
                disabled={selectedIds.length > 0}
                className={`text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border font-normal whitespace-nowrap ${
                  selectedIds.length > 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]'
                }`} 
                onClick={handleDeleteAllClick}
              >
                Delete All
              </button>
              <button 
                disabled={selectedIds.length > 0}
                className={`text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border font-normal whitespace-nowrap ${
                  selectedIds.length > 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]'
                }`} 
                onClick={handleMarkAllAsReadClick}
              >
                Mark All As Read
              </button>
              <button 
                disabled={selectedIds.length > 0}
                className={`text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border font-normal whitespace-nowrap ${
                  selectedIds.length > 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]'
                }`} 
                onClick={handleShowOnlyUnread}
              >
                {showUnreadOnly ? 'Show All' : 'Show Only Unread'}
              </button>
            </div>
          </div>

          <div className="bg-white mx-[30px] my-0 rounded border border-[#e0e0e0] shadow-[0_1px_3px_rgba(0,0,0,0.05)] md:mx-[30px] mx-[15px]" ref={contentRef}>
            {loading ? (
              <div className="text-center py-[60px] px-0 text-[#aaa] text-[14px]">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="text-center py-[60px] px-0 text-[#aaa] text-[14px]">No messages available</div>
            ) : (
              messages.map((message) => {
                const originalId = notifications.find(n => n.id === message.id || (n.title === message.subject && n.content === message.content && n.created_at === message.created_at))?.id;
                const isExpanded = expandedNotifications[message.id];

                return (
                  <div
                    key={message.id}
                    className={`border-b border-[#f0f0f0] transition-colors duration-200 last:border-b-0 ${!message.isRead ? 'bg-[#e6f4ff]' : 'bg-white'}`}
                  >
                    <div className="flex items-start py-5 px-5 hover:bg-[#fcfcfc]/50">
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          checked={message.selected}
                          onChange={() => handleSelectMessage(originalId || message.id)}
                          className="w-[18px] h-[18px] mr-[15px] cursor-pointer accent-[#eb910c]"
                        />
                      </div>

                      <div className="flex-1 cursor-pointer" onClick={() => handleToggleExpand(message.id, message.isRead, originalId)}>
                        <h3 className={`m-0 text-[16px] leading-[1.2] ${!message.isRead ? 'font-bold text-black' : 'font-semibold text-[#333]'}`}>
                          {message.subject}
                        </h3>

                        <div
                          style={{
                            display: 'grid',
                            gridTemplateRows: isExpanded ? '1fr' : '0fr',
                            transition: 'grid-template-rows 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            opacity: isExpanded ? 1 : 0
                          }}
                          className="transition-opacity duration-300"
                        >
                          <div className="overflow-hidden">
                            <div className="mt-2 text-[13px] text-[#666] font-medium">
                              {message.formattedDate}, {message.formattedTime}
                            </div>
                            <div className="mt-2 text-[14px] text-[#444] leading-[1.6] whitespace-normal"
                              dangerouslySetInnerHTML={{ __html: message.content }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="ml-3">
                        <button
                          className="bg-[#dc3545] text-white border-none rounded-[4px] w-[35px] h-[35px] flex items-center justify-center cursor-pointer transition-all duration-200 hover:bg-[#c82333] hover:shadow-md active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMessageClick(originalId || message.id);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTrashAlt}
                            style={{ color: 'white', fontSize: '15px' }}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {pagination.totalPages > 1 && (!showUnreadOnly || unreadCount > limit) && (
            <div className="m-0 whitespace-nowrap text-right h-[39px] flex justify-between items-center py-[15px] px-[30px] mt-[15px] md:flex-row flex-col md:gap-0 gap-[15px]">
              <div className="text-[13px] text-[#555]">
                {pagination.total > 0 ? (
                  `Showing ${(pagination.page - 1) * pagination.limit + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} entries`
                ) : (
                  'No entries to show'
                )}
              </div>
              <ul className="inline-block pl-0 my-0.5 rounded list-none">
                <li className="inline first:ml-0">
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] no-underline border border-[#ddd] rounded-l text-[14px] transition-colors duration-200 ${pagination.page <= 1 ? 'bg-[#fafafa] text-[#777] cursor-not-allowed' : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
                      }`}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(pagination.totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  if (pagination.totalPages > 7) {
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.totalPages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                    ) {
                      return (
                        <li key={pageNum} className="inline">
                          <button
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] border border-[#ddd] text-[14px] transition-colors duration-200 ${pagination.page === pageNum ? 'text-white bg-[#eb910c]' : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
                              }`}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    }
                    if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                      return <li key={pageNum} className="inline"><span className="relative float-left py-0.5 px-3 -ml-px border border-[#ddd] bg-white text-[14px]">...</span></li>;
                    }
                    return null;
                  }

                  return (
                    <li key={pageNum} className="inline">
                      <button
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] border border-[#ddd] text-[14px] transition-colors duration-200 ${pagination.page === pageNum ? 'text-white bg-[#eb910c]' : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
                          }`}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                <li className="inline last:rounded-r">
                  <button
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] no-underline border border-[#ddd] rounded-r text-[14px] transition-colors duration-200 ${pagination.page >= pagination.totalPages ? 'bg-[#fafafa] text-[#777] cursor-not-allowed' : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
                      }`}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Copyright Section */}
      <div className="pt-0 p-[15px] mr-auto ml-auto px-[15px] bg-jet-gray-100 w-full">
        <div className="bg-white p-[15px] text-black border-t-0 text-center shadow-none pb-[10px] rounded-[5px]">
          Copyright © 2026 Jetscanner360. All rights reserved.
        </div>
      </div>

      <UniversalConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        type="delete"
        title={getDeleteModalContent().title}
        message={getDeleteModalContent().message}
        theme="modern"
      />
    </div>
  );
};

export default Messages;
